/**
 * What a published link's people did — the segment box's sunburst, for the
 * people a link brought: whose first touch was the link, or whose session in
 * the window came through it. Built on the fly from the link, never stored as
 * a segment. Backend `getSegmentActivity.ts` (`?link=`).
 *
 * With `slug` given it is fixed to that link (the link's own page); without, a
 * picker of the published links.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InfoTip, Select } from '@shared-ui/components';
import { getLinkActivity } from '../../../api/segmentsApi';
import { publishedLinksApi } from '../../../api/publishedLinksApi';
import PathSunburst, { type SunburstVocab } from '../shared/PathSunburst';

const VOCAB: SunburstVocab = {
  nameOf: path => (path.length === 0 ? 'Everything they did' : path[path.length - 1]),
  sectionHeader: 'Site',
  childNoun: 'kinds',
  sizeLabel: 'Times',
  sizeUnit: 'times done',
  countLabel: 'People',
  countDetail: 'People',
  hint: where => (
    <>
      Inner ring: {where === 'the site' ? 'which site they did it on' : `what they did on ${where}`}. Outer ring:
      each kind of thing, or which one. Slices are sized by how many times it was done; hover one for how many
      people did it. Click a slice with more under it to open it.
    </>
  ),
};

export default function LinkActivity({ from, to, slug: fixed }: { from: Date; to: Date; slug?: string }) {
  const links = useQuery({ queryKey: ['publishedLinks'], queryFn: publishedLinksApi.list, enabled: !fixed });
  const [picked, setPicked] = useState('');
  const slug = fixed ?? picked;
  const activity = useQuery({
    queryKey: ['link-activity', slug, from.toISOString(), to.toISOString()],
    queryFn: () => getLinkActivity(slug, from, to),
    enabled: !!slug,
  });

  return (
    <section className="sa-panel">
      <h2>
        {fixed ? 'What this link’s people did' : 'What a published link’s people did'}
        <InfoTip>
          <span className="sa-tip-p">
            The people a published link brought: those whose first touch was the link, and anyone whose session in
            this window came through it — a returning person’s own follow, which their first touch never shows.
          </span>
          <span className="sa-tip-p">
            Worked out from the link each time, not stored as a segment. For a named, reusable set — say, the
            link’s people who also used the lab — make a segment on <code>link</code> under Acquisition → Segments.
          </span>
        </InfoTip>
      </h2>
      {!fixed && (
        <div className="seg-activity-pick">
          <Select value={picked} onChange={e => setPicked(e.target.value)} aria-label="Published link">
            <option value="">Choose a published link…</option>
            {(links.data?.links ?? []).map(l => (
              <option key={l.slug} value={l.slug}>{l.slug}{l.campaign ? ` · ${l.campaign}` : ''}</option>
            ))}
          </Select>
        </div>
      )}
      {activity.isLoading && slug && <p className="sa-empty">Loading…</p>}
      {activity.error && <p className="sa-error">{(activity.error as Error).message}</p>}
      {activity.data && (
        activity.data.people === 0
          ? <p className="sa-empty">Nobody this link brought was active in the window.</p>
          : (
            <>
              <p className="sa-note">
                <strong>{activity.data.people.toLocaleString()}</strong> of the{' '}
                {activity.data.active.toLocaleString()} people active in this window came through this link.
              </p>
              <PathSunburst root={activity.data.tree} vocab={VOCAB} />
            </>
          )
      )}
    </section>
  );
}
